import {API_URL, FIREBASE_URL, NEW_API_URL} from '@/config/api';
import {getAccessToken, refreshAccessToken} from '@/utils/token-manager';
import axios, {AxiosInstance, AxiosResponse} from 'axios';
import {getAuth, signOut} from 'firebase/auth';

export const firebaseClient = axios.create({
  baseURL: FIREBASE_URL,
  timeout: 1000 * 60 * 5 // 5mins
});

firebaseClient.interceptors.request.use(
  async (config) => {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();

    // append auth param
    if (config.url?.includes('?')) {
      config.url = `${config.url}&auth=${token}`;
    } else {
      config.url = `${config.url}?auth=${token}`;
    }

    return config;
  },
  async (error: any) => {
    await Promise.reject(error);
  }
);

export const axiosClient = axios.create({
  baseURL: API_URL,
  timeout: 1000 * 60 * 5 // 5mins
});

export const newAxiosClient = axios.create({
  baseURL: NEW_API_URL,
  timeout: 1000 * 60 * 5 // 5mins
});

const attachRequestInterceptor = (client: AxiosInstance) => {
  client.interceptors.request.use(
    async (config) => {
      if (!config.headers) {
        return config;
      }
      const token = getAccessToken();
      if (token) {
        config.headers = {
          Authorization: `Bearer ${token}`,
          ...config.headers?.common,
          ...config.headers
        };
      }

      return config;
    },
    async (error: any) => {
      return await Promise.reject(error);
    }
  );
};

const attachResponseInterceptor = (
  client: AxiosInstance,
  withRefreshLogic: boolean = false
) => {
  client.interceptors.response.use(
    async (response: AxiosResponse) => {
      if (response?.data) {
        return response.data;
      }
      return response;
    },
    async (error: any) => {
      const originalRequest = error.config;
      try {
        if (
          !error?.toJSON ||
          error.toJSON().code === 'ERR_NETWORK' ||
          error.toJSON().code === 'ERR_CANCELED'
        ) {
          return {
            ...error,
            error: true
          };
        }
        if (
          error.response &&
          [401, 403].includes(error.response.status) &&
          withRefreshLogic &&
          !originalRequest._retry &&
          originalRequest.headers.Authorization
        ) {
          originalRequest._retry = true;

          const newAccessToken = await refreshAccessToken();

          return await client.request({
            ...originalRequest,
            headers: {
              ...originalRequest.headers,
              Authorization: `Bearer ${newAccessToken}`
            }
          });
        }

        if (error?.response?.data && typeof error.response.data === 'object') {
          return {error: true, ...error.response.data};
        }
        return {
          ...error,
          error: true
        };
      } catch (exception) {
        if (
          withRefreshLogic &&
          originalRequest._retry &&
          error.response &&
          [401, 403].includes(error.response.status)
        ) {
          localStorage.clear();
          await signOut(getAuth());
        }
        return {
          ...error,
          error: true
        };
      }
    }
  );
};

attachRequestInterceptor(axiosClient);
attachResponseInterceptor(axiosClient, true);

attachRequestInterceptor(newAxiosClient);
attachResponseInterceptor(newAxiosClient, true);

attachResponseInterceptor(firebaseClient);

export default axiosClient;
